import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * True while the viewport is being resized (rotation, zoom, window drag).
   * @type {boolean}
   */
  #resizing = false;

  /**
   * Clears #resizing once the viewport has been still for a moment.
   * @type {number | null}
   */
  #resizeNoiseTimeout = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.#setState('stickyState', isIntersecting ? 'inactive' : 'active');
        this.#mirrorStickyState();
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // Once the menu hides, the overflow-list is display:none and can no longer
      // measure itself, so it can never clear its own minimum-reached flag —
      // it would stay latched from the width that hid the menu. Reset it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and recreates the IntersectionObserver when the
   * viewport crosses `scrollContainerMediaQuery`. This dates from the two-container
   * era, when the page scrolled a div on desktop and the document on mobile and the
   * cached bindings went stale on resize. Since the move to root scrolling both
   * helpers in assets/scroll-container.js are width-independent (the target is always
   * `document`, the observer root always the viewport), so this rebinds to the very
   * same target — a no-op today, kept because nothing has verified it is safe to drop.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  /**
   * Поворот экрана и любой другой resize меняют раскладку, из-за чего scrollTop сдвигается сам по
   * себе — это НЕ жест пользователя. Замер 30.08 на нетач-устройстве 700x1000 -> 1000x700, когда
   * шапка была показана прокруткой вверх: scrollY 1550 -> 1562, то есть +12px, обработчик прочитал
   * это как «прокрутил вниз» и увёл шапку — top -1 -> -1527, opacity 1 -> 0.44 и дальше в ноль.
   * Владелец описал это как «поворачиваю телефон, шапка мерцает, появляется и пропадает».
   * Ожидание простое: в каком состоянии шапка была до поворота, в таком должна и остаться.
   *
   * Тот же класс шума, что уже отфильтрован ниже для scroll-lock и для поиска. Разница одна:
   * там позиция реально не менялась и #lastScrollTop трогать нельзя, а здесь она изменилась —
   * поэтому запоминаем новую, чтобы первый настоящий жест после поворота мерился от неё.
   */
  #handleViewportResize = () => {
    this.#resizing = true;
    if (this.#resizeNoiseTimeout) clearTimeout(this.#resizeNoiseTimeout);
    this.#resizeNoiseTimeout = setTimeout(() => {
      this.#resizing = false;
      this.#resizeNoiseTimeout = null;
      this.#lastScrollTop = getScrollTop();
    }, 250);
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    // A drawer or modal owns the page scroll (html[scroll-lock]). The shopper cannot scroll the
    // document at all in that state, so anything arriving here is layout noise rather than a
    // gesture: locking takes the classic scrollbar away, the page reflows by its width, and scroll
    // anchoring nudges scrollTop to compensate. That nudge read as "scrolling down" and faded the
    // sticky header out for as long as the drawer stayed open, then restored it on close. A drawer
    // is an overlay drawn on top of everything — the header underneath it has no reason to move.
    // Freeze the whole state, #lastScrollTop included, so the first real gesture after the drawer
    // closes is measured from where the shopper actually left the page.
    //
    // The search panel locks the page too, through its own `r69-search-lock` CLASS rather than
    // this attribute (it predates the shared owner-set in utilities.js). Without it in the guard,
    // closing the search fired a noise scroll that read as "scrolling down" and faded a header the
    // shopper had scrolled up to reveal — it vanished on close although nothing was scrolled.
    // Leaving #lastScrollTop untouched is what makes the close clean: the position never actually
    // changed, so the first event after the unlock measures a delta of zero and changes nothing.
    const root = document.documentElement;
    if (root.hasAttribute('scroll-lock') || root.classList.contains('r69-search-lock')) return;

    // Поворот экрана / зум / перетаскивание окна: см. #handleViewportResize выше.
    if (this.#resizing) {
      this.#lastScrollTop = getScrollTop();
      return;
    }

    const scrollTop = getScrollTop();
    // Ignore tiny scrollTop jitter (< 3px). A late-loading / resizing section below can make scroll
    // anchoring nudge scrollTop backward by a few px for one frame; without this guard that reads as
    // "scrolling up" and flickers the sticky header in for a frame while you're scrolling down.
    if (Math.abs(scrollTop - this.#lastScrollTop) < 3) {
      this.#lastScrollTop = scrollTop;
      return;
    }
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.#setState('scrollDirection', 'none');
      } else if (isScrollingUp) {
        this.#setState('scrollDirection', 'up');
      } else {
        this.#setState('scrollDirection', 'down');
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.#setState('stickyState', 'inactive');
        this.#setState('scrollDirection', 'none');
      } else {
        // show sticky header when scrolling up
        this.#setState('stickyState', 'active');
        this.#setState('scrollDirection', 'up');
      }
    } else if (this.dataset.stickyState === 'active') {
      this.#setState('scrollDirection', 'none');

      this.#setState('stickyState', 'idle');
    } else {
      this.#setState('scrollDirection', 'none');
      this.#setState('stickyState', 'idle');
    }

    this.#mirrorStickyState();
    this.#lastScrollTop = scrollTop;
  };

  /**
   * Пишет data-атрибут шапки только при смене значения. Запись того же значения — всё равно
   * мутация DOM: её получает каждый MutationObserver, включая запись сессии Clarity, а браузер
   * пересчитывает стили по селекторам этого атрибута. Обработчик прокрутки зовёт это на каждом
   * кадре, и почти всегда с тем же значением: замер 01.11.2026 на коллекции — 270 записей каждого
   * из двух атрибутов за 30 с прокрутки, из них настоящих смен 2–3.
   * @param {'scrollDirection' | 'stickyState'} key
   * @param {string} value
   */
  #setState(key, value) {
    if (this.dataset[key] !== value) this.dataset[key] = value;
  }

  /**
   * PERF: mirror the sticky state as a body class. Anything outside the header that needs to know
   * (the PDP's sticky details column) used to read it with
   * `body:has(#header-group #header-component[data-sticky-state='active'])`, and a :has() anchored
   * on body is re-evaluated on EVERY DOM insertion anywhere on the page — measured at 29-46ms per
   * insertion on a throttled device. classList.toggle with an explicit force flag does not touch
   * the DOM when the state is unchanged, so continuous scrolling costs nothing.
   */
  #mirrorStickyState = () => {
    document.body.classList.toggle('r69-header-sticky-active', this.dataset.stickyState === 'active');
  };

  connectedCallback() {
    super.connectedCallback();
    // Re-sync the mirrored header flags: in the theme editor the header re-renders with new
    // settings (transparent/sticky can flip) without a page reload, so the classes theme.liquid
    // set at boot would otherwise go stale.
    window.r69MirrorHeaderFlags?.();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
      window.addEventListener('resize', this.#handleViewportResize, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    window.removeEventListener('resize', this.#handleViewportResize);
    if (this.#resizeNoiseTimeout) {
      clearTimeout(this.#resizeNoiseTimeout);
      this.#resizeNoiseTimeout = null;
    }
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

// Anchor the shopify-account dialog to the bottom of the account button itself.
document.addEventListener(
  'open',
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

    const bottom = Math.max(0, Math.round(target.getBoundingClientRect().bottom));
    target.style.setProperty('--account-dialog-top', `${bottom}px`);
  },
  true
);

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
